import { BaseElement } from 'base';

const ROTATION_DELAY = 5000;
const ITEM = '[data-promo-bar-item]';

class PromoBar extends BaseElement {
  #items = [];
  #index = 0;
  #timer = 0;
  #toggle = null;
  #userPaused = false;
  #hovered = false;
  #focused = false;
  #selected = false;

  onConnect() {
    this.#hovered = false;
    this.#focused = false;
    this.#selected = false;
    this.#items = this.qa(ITEM);
    this.#index = Math.max(
      0,
      this.#items.findIndex(item => !item.inert)
    );
    this.#toggle = this.q('[data-promo-bar-toggle]');

    this.listen(this, 'click', this.#onClick);
    this.cleanup(() => {
      clearTimeout(this.#timer);
      this.#timer = 0;
    });
    if (this.#items.length < 2) return;

    this.listen(this, 'pointerenter', this.#onPointerEnter);
    this.listen(this, 'pointerleave', this.#onPointerLeave);
    this.listen(this, 'focusin', this.#onFocusIn);
    this.listen(this, 'focusout', this.#onFocusOut);
    this.listen(this, 'shopify:block:select', this.#onBlockSelect);
    this.listen(this, 'shopify:block:deselect', this.#onBlockDeselect);
    this.#renderToggle();
    this.#sync();
  }

  #onClick(event) {
    if (event.target.closest('[data-promo-bar-toggle]')) {
      this.#userPaused = !this.#userPaused;
      this.#renderToggle();
      this.#sync();
      return;
    }
    if (event.target.closest('[data-promo-bar-klaviyo]')) this.#openForm();
  }

  #openForm() {
    const formId = this.dataset.klaviyoForm;
    if (!formId || window.Shopify?.designMode) return;
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['openForm', formId]);
  }

  #onPointerEnter(event) {
    if (event.pointerType !== 'mouse') return;
    this.#hovered = true;
    this.#sync();
  }

  #onPointerLeave() {
    this.#hovered = false;
    this.#sync();
  }

  #onFocusIn(event) {
    if (!event.target.closest(ITEM)) return;
    this.#focused = true;
    this.#sync();
  }

  #onFocusOut(event) {
    const next = event.relatedTarget;
    if (next && this.contains(next) && next.closest(ITEM)) return;
    this.#focused = false;
    this.#sync();
  }

  #onBlockSelect(event) {
    const index = this.#items.indexOf(event.target.closest(ITEM));
    if (index < 0) return;
    this.#selected = true;
    this.#show(index);
    this.#sync();
  }

  #onBlockDeselect() {
    this.#selected = false;
    this.#sync();
  }

  #renderToggle() {
    if (!this.#toggle) return;
    const { labelPause, labelPlay } = this.#toggle.dataset;
    this.#toggle.toggleAttribute('data-paused', this.#userPaused);
    this.#toggle.setAttribute(
      'aria-label',
      this.#userPaused ? labelPlay : labelPause
    );
  }

  #show(index) {
    this.#index = index;
    this.#items.forEach((item, position) => {
      item.inert = position !== index;
    });
  }

  #sync() {
    const running =
      this.#items.length > 1 &&
      !this.#userPaused &&
      !this.#hovered &&
      !this.#focused &&
      !this.#selected;
    if (!running) {
      clearTimeout(this.#timer);
      this.#timer = 0;
      return;
    }
    if (!this.#timer)
      this.#timer = setTimeout(() => this.#advance(), ROTATION_DELAY);
  }

  #advance() {
    this.#timer = 0;
    this.#show((this.#index + 1) % this.#items.length);
    this.#sync();
  }
}

if (!customElements.get('promo-bar'))
  customElements.define('promo-bar', PromoBar);
